
import React from 'react';
import { cn } from '@/lib/utils';

// Export the ButtonProps interface
export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  icon?: React.ReactNode;
  href?: string;
}

// Export the buttonVariants function for use in other components
export const buttonVariants = ({
  variant = 'primary',
  size = 'default',
  className = '',
}: {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
} = {}) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
  
  const variantClasses = {
    primary: "bg-orcsys-400 text-white shadow hover:bg-orcsys-500 focus-visible:ring-orcsys-400",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-ring",
    outline: "border border-orcsys-400 text-orcsys-400 bg-transparent hover:bg-orcsys-50 focus-visible:ring-orcsys-400",
    ghost: "hover:bg-orcsys-50 text-orcsys-500 hover:text-orcsys-600"
  };
  
  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 px-3",
    lg: "h-11 px-8",
    icon: "h-10 w-10 p-2"
  };

  return cn(baseClasses, variantClasses[variant], sizeClasses[size], className);
};

// Make ButtonComponent forward refs
const ButtonComponent = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'size'>
>(({
  children,
  variant = 'primary',
  size = 'default',
  className,
  onClick,
  type = 'button',
  disabled = false,
  icon,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonVariants({ variant, size, className })}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
});

ButtonComponent.displayName = 'Button';

// Make ButtonLink forward refs
const ButtonLink = React.forwardRef<
  HTMLAnchorElement,
  ButtonProps & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'type' | 'size'>
>(({
  children,
  variant = 'primary',
  size = 'default',
  className,
  href = "#",
  icon,
  ...props
}, ref) => {
  return (
    <a
      ref={ref}
      href={href}
      className={buttonVariants({ variant, size, className })}
      {...props}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </a>
  );
});

ButtonLink.displayName = 'ButtonLink';

// Create a Button object with the Link component attached
export const Button = Object.assign(ButtonComponent, { Link: ButtonLink });

// Default export stays the same for backward compatibility
export default Button;
